/**
 * Typed front end for the pipeline worker.
 *
 * Keeps worker lifecycle and message plumbing out of the Svelte components:
 * they call `run`, receive callbacks, and call `cancel`.
 */

import type {
  CropRect,
  IterationProgress,
  PipelineEvent,
  PipelineParams,
  PipelineStage,
  PreviewFrame,
  VectorizationResult,
} from '$lib/types'

export interface RunHandlers {
  onStage?: (stage: PipelineStage, detail?: string) => void
  onIteration?: (progress: IterationProgress) => void
  onPreview?: (frame: PreviewFrame) => void
}

export class ConversionCancelled extends Error {
  constructor() {
    super('Conversion cancelled')
    this.name = 'ConversionCancelled'
  }
}

export class ConversionFailed extends Error {
  constructor(
    message: string,
    readonly recoverable: boolean,
  ) {
    super(message)
    this.name = 'ConversionFailed'
  }
}

export interface RunOptions extends RunHandlers {
  /** Convert only this region of the image, in source pixels. */
  crop?: CropRect | null
}

export interface PipelineClient {
  run(
    image: ImageBitmap | ImageData,
    params: PipelineParams,
    options?: RunOptions,
  ): Promise<VectorizationResult>
  cancel(): void
  readonly busy: boolean
  dispose(): void
}

export function createPipelineClient(worker: Worker = spawnWorker()): PipelineClient {
  let nextId = 1
  let activeId: number | null = null

  return {
    get busy() {
      return activeId !== null
    },

    run(image, params, options = {}) {
      const handlers: RunHandlers = options
      if (activeId !== null) {
        return Promise.reject(new Error('A conversion is already running'))
      }
      const id = nextId++
      activeId = id

      return new Promise<VectorizationResult>((resolve, reject) => {
        const finish = (fn: () => void) => {
          worker.removeEventListener('message', onMessage)
          worker.removeEventListener('error', onError)
          activeId = null
          fn()
        }

        const onMessage = (event: MessageEvent<PipelineEvent>) => {
          const message = event.data
          switch (message.type) {
            case 'stage':
              handlers.onStage?.(message.stage, message.detail)
              break
            case 'iteration':
              handlers.onIteration?.(message)
              break
            case 'preview':
              handlers.onPreview?.(message)
              break
            case 'done':
              finish(() => resolve(message.result))
              break
            case 'cancelled':
              finish(() => reject(new ConversionCancelled()))
              break
            case 'error':
              finish(() => reject(new ConversionFailed(message.message, message.recoverable)))
              break
          }
        }

        const onError = (event: ErrorEvent) => {
          finish(() => reject(new ConversionFailed(event.message || 'The worker crashed', false)))
        }

        worker.addEventListener('message', onMessage)
        worker.addEventListener('error', onError)

        // The bitmap is transferred, not copied: it can be several megabytes.
        const transfer = 'close' in image ? [image] : []
        worker.postMessage(
          {
            type: 'start',
            id,
            image,
            // Copied field by field, not passed through: callers hand over
            // reactive state, and a framework proxy is not structured-cloneable
            // — `postMessage` rejects it outright.
            params: plain(params),
            crop: options.crop ? plain(options.crop) : undefined,
          },
          transfer,
        )
      })
    },

    cancel() {
      if (activeId !== null) worker.postMessage({ type: 'cancel', id: activeId })
    },

    dispose() {
      worker.terminate()
      activeId = null
    },
  }
}

/**
 * A shallow copy with no proxy and no prototype surprises.
 *
 * Everything crossing to the worker is flat data, so a spread is enough; the
 * point is that the object handed to `postMessage` is never the caller's own
 * reactive one.
 */
function plain<T extends object>(value: T): T {
  return { ...value }
}

function spawnWorker(): Worker {
  return new Worker(new URL('./worker.ts', import.meta.url), {
    type: 'module',
    name: 'vectorize-pipeline',
  })
}
